import React, { useRef, useEffect, useState } from "react";
import {
  Flex,
  Input,
  IconButton,
  Tooltip,
  Text,
  Box,
  Button,
  VStack,
  Link,
  HStack,
  useBreakpointValue,
  Spacer,
  Icon as ChakraIcon,
} from "@chakra-ui/react";
import { MdSend } from "react-icons/md";
import { FaHotel, FaPlane } from "react-icons/fa";
import { ReactComponent as FlagIcon } from "../../assets/icons/flag.svg";
import { ReactComponent as CabinIcon } from "../../assets/icons/home_icon.svg";
import { ReactComponent as TransportationIcon } from "../../assets/icons/transportation.svg";
import { FiMessageSquare } from "react-icons/fi";
import { FaCheck } from "react-icons/fa";
import { formatDate } from "../../helpers/utils";

import Translate from "../../components/LanguageTranslation/Translate";
import { ArrowUpIcon, ChatIcon } from "@chakra-ui/icons";
import { SurveyPopup } from "../Popups";

const MessageInput = ({
  message,
  setMessage,
  handleSendMessage,
  colorMode,
  suggestions,
  handleSuggestionClick,
  isLoading,
  generalInfoAdded,
  activeTab,
  setActiveTab,
  showItineraryOnMobile,
  setShowItineraryOnMobile,
  showScrollUp,
  scrollToTop,
  generalInfo,
  hasHomeToGoData,
  showNextTabAnimation,
}) => {
  // console.log("🔍 MessageInput.js - props - hasHomeToGoData:", hasHomeToGoData);
  const inputRef = useRef(null);
  const isBaseScreen = useBreakpointValue({ base: true, md: false });
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  const tabOrder = [
    "general_conversation",
    "transportation_conversation",
    "accommodation_conversation",
    "explore_conversation",
  ];

  const currentTabIndex = tabOrder.indexOf(activeTab);
  const prevTab =
    tabOrder[(currentTabIndex - 1 + tabOrder.length) % tabOrder.length];
  const nextTab = tabOrder[(currentTabIndex + 1) % tabOrder.length];

  // Check if a tab is completed (next tab is unlocked)
  const isTabCompleted = (tabKey) => {
    const currentIndex = tabOrder.indexOf(tabKey);
    const nextTabKey = tabOrder[currentIndex + 1];

    if (!nextTabKey) return false; // Last tab can't be "completed"

    return isTabUnlocked(nextTabKey);
  };

  // Progressive unlocking logic based on generalInfo data
  const isTabUnlocked = (tabKey) => {
    // General tab is always unlocked
    if (tabKey === "general_conversation") {
      return true;
    }

    // If no generalInfo, only general tab is unlocked
    if (!generalInfo || Object.keys(generalInfo).length === 0) {
      return false;
    }

    // Transportation tab: unlocked when generalInfo has any data
    if (tabKey === "transportation_conversation") {
      return generalInfo && Object.keys(generalInfo).length > 0;
    }

    // Accommodation tab: unlocked when generalInfo includes flight_info
    if (tabKey === "accommodation_conversation") {
      return (
        generalInfo &&
        generalInfo.flight_info &&
        generalInfo.flight_info.length > 0
      );
    }

    // Activities tab: unlocked when hasHomeToGoData is true
    if (tabKey === "explore_conversation") {
      // console.log("🔍 MessageInput.js - isTabUnlocked - explore_conversation - hasHomeToGoData:", hasHomeToGoData);
      return hasHomeToGoData;
    }

    return false;
  };

  const getTabIcon = (tabKey) => {
    const iconMap = {
      general_conversation: (
        <ChakraIcon as={FiMessageSquare} boxSize={4} color="white" />
      ),
      transportation_conversation: (
        <ChakraIcon as={TransportationIcon} boxSize={4} fill="white" />
      ),
      accommodation_conversation: (
        <ChakraIcon as={CabinIcon} boxSize={4} fill="white" />
      ),
      explore_conversation: (
        <ChakraIcon as={FlagIcon} boxSize={4} fill="white" />
      ),
    };

    return iconMap[tabKey] || iconMap.general_conversation;
  };

  const handlePrevClick = () => {
    setActiveTab(prevTab);
  };

  const handleNextClick = () => {
    setActiveTab(nextTab);
  };

  const handleShowItinerary = () => {
    setShowItineraryOnMobile(true);
  };

  // Show feedback icon only when all tabs are unlocked
  const allTabsUnlocked =
    isTabUnlocked("transportation_conversation") &&
    isTabUnlocked("accommodation_conversation") &&
    isTabUnlocked("explore_conversation");

  return (
    <VStack p={4} pt={2} maxH="18vh" justifyContent="space-between" spacing={2}>
      {/* Suggestions Box */}
      {generalInfoAdded && tabOrder.includes(activeTab) && (
        <Box
          w="100%"
          minH={{ base: "25px", md: "45px" }}
          display="flex"
          flexWrap="wrap"
          justifyContent="left"
          alignItems="flex-end"
          overflowX="auto"
          sx={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
          // noOfLines="1"
        >
          {currentTabIndex !== 0 && (
            <Button
              onClick={handlePrevClick}
              size={{ base: "xs", md: "sm" }}
              colorScheme="customGreen"
              variant="solid"
              borderRadius="lg"
              px={6}
              py={2}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              _hover={{
                transform: "translateY(-1px)",
                boxShadow: "md",
              }}
              _active={{
                transform: "translateY(0)",
              }}
              transition="all 0.2s"
              leftIcon={getTabIcon(prevTab)}
            >
              <Translate text="Prev Tab" />
            </Button>
          )}
          {currentTabIndex !== tabOrder.length - 1 && (
              <Button
                onClick={handleNextClick}
                size={{ base: "xs", md: "sm" }}
                colorScheme="customGreen"
                variant="solid"
                borderRadius="lg"
                px={6}
                py={2}
                fontSize="sm"
                fontWeight="medium"
                _hover={{
                  transform: "translateY(-1px)",
                  boxShadow: "md",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                transition="all 0.2s"
                leftIcon={getTabIcon(nextTab)}
                sx={{
                  animation:
                    showNextTabAnimation &&
                    activeTab === "general_conversation" &&
                    currentTabIndex === 0
                      ? "dance 0.6s ease-in-out 6"
                      : "none",
                  "@keyframes dance": {
                    "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
                    "25%": { transform: "translateY(-4px) rotate(-2deg)" },
                    "50%": { transform: "translateY(-6px) rotate(0deg)" },
                    "75%": { transform: "translateY(-4px) rotate(2deg)" },
                  },
                }}
              >
                <Translate text="Next Tab" />
              </Button>
            )}

          {isBaseScreen && (
            <Button
              onClick={handleShowItinerary}
              size={{ base: "xs", md: "sm" }}
              colorScheme="customGreen"
              variant="solid"
              borderRadius="lg"
              px={6}
              py={2}
              fontSize="sm"
              fontWeight="medium"
              _hover={{
                transform: "translateY(-1px)",
                boxShadow: "md",
              }}
              _active={{
                transform: "translateY(0)",
              }}
              transition="all 0.2s"
              ml={2}
            >
              <Translate text="Show Itinerary" />
            </Button>
          )}

          <Tooltip label="Feedback">
          <IconButton
            aria-label="Feedback"
            icon={<ChatIcon />}
            onClick={() => setFeedbackOpen(true)}
            size={{ base: "xs", md: "sm" }}
            bg="#FFC719"
            color="black"
            variant="solid"
            borderRadius="lg"
            _hover={{
              transform: "translateY(-1px)",
              boxShadow: "md",
              bg: "#FFC719",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            transition="all 0.2s"
             ml={2}
          />
        </Tooltip>

          <Spacer />
          {/* ② Scroll‑up button */}
          {showScrollUp && (
            <IconButton
              display={{ base: "none", md: "block" }}
              icon={<ArrowUpIcon boxSize={5} />}
              size="sm"
              bg="#1A202C"
              color="white"
              _hover={{ bg: "#1A202C" }}
              borderRadius="full"
              zIndex="1200"
              onClick={scrollToTop}
            />
          )}
        </Box>
      )}
      
      {/* Feedback Icon - Always Visible in Same Row */}
      {!generalInfoAdded && (
      <Box
        w="100%"
        minH={{ base: "25px", md: "45px" }}
        display="flex"
        flexWrap="wrap"
        justifyContent="left"
        alignItems="flex-end"
        overflowX="auto"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Tooltip label="Feedback">
          <IconButton
            aria-label="Feedback"
            icon={<ChatIcon />}
            onClick={() => setFeedbackOpen(true)}
            size={{ base: "xs", md: "sm" }}
            bg="#FFC719"
            color="black"
            variant="solid"
            borderRadius="lg"
            _hover={{
              transform: "translateY(-1px)",
              boxShadow: "md",
              bg: "#FFC719",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            transition="all 0.2s"
          />
        </Tooltip>
      </Box>
      )}
      <Flex as="form" width="100%" onSubmit={(e) => e.preventDefault()}>
        <Input
          placeholder={Translate({
            text: "Ask anything...",
            as: "string",
          })}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          mr={2}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          isDisabled={isLoading}
          ref={inputRef}
        />

        <Tooltip label="Send message" fontSize="md">
          <IconButton
            aria-label="Send"
            icon={<MdSend />}
            onClick={handleSendMessage}
            colorScheme="alpha"
            textColor={colorMode === "dark" ? "white" : "black"}
            isDisabled={isLoading}
          />
        </Tooltip>
      </Flex>
      <SurveyPopup
        isOpen={isFeedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
      <HStack
        display={{ base: "block", md: "none" }}
        spacing={0}
        justify="center"
        width="100%"
        mt={0}
      >
        <Text fontSize="xs" mb={0}>
          <Translate text="By using this chat, you agree to our" />{" "}
          <Link
            fontSize="xs"
            href="/policies/terms-of-service"
            textDecoration="underline"
          >
            <Translate text="Terms of Service" />
          </Link>{" "}
          <Translate text="and" />{" "}
          <Link
            fontSize="xs"
            href="/policies/privacy-policy"
            textDecoration="underline"
          >
            <Translate text="Privacy Policy" />
          </Link>
          .
        </Text>
      </HStack>
      <Box display={{ base: "block", md: "none" }} mx={2} mt={0}>
        <Text
          textAlign="justify"
          lineHeight="shorter"
          fontSize={{ base: "xs", md: "10px" }}
          fontWeight="light"
          color="gray.500"
          mb={0}
        >
          <Translate text="Please note: You will communicate with an assistant that uses artificial intelligence (AI). The information you receive may contain errors. We recommend that you always check prices and travel advice via the provider's website and with official authorities before making a booking. NOMAD360™ does not guarantee the accuracy, completeness or suitability of the information provided and expressly excludes any liability for this." />
        </Text>
      </Box>
      {showScrollUp && (
        <IconButton
          display={{ base: "block", md: "none" }}
          icon={<ArrowUpIcon boxSize={5} />}
          position={{ base: "fixed", md: "relative" }}
          bottom={[32, undefined]} // base = 16, md+ = undefined
          right={[2, undefined]}
          size="sm"
          bg="#1A202C"
          color="white"
          _hover={{ bg: "#1A202C" }}
          borderRadius="full"
          zIndex="1200"
          onClick={scrollToTop}
        />
      )}
    </VStack>
  );
};

export default MessageInput;
